<template>
  <div class="min-h-[60vh] flex items-center justify-center animate-fade-in">
    <div class="glass rounded-xl p-6 w-[520px] max-w-[92vw]">
      <h2 class="text-xl font-semibold text-white mb-2">Complete your account</h2>
      <p class="text-slate-400 text-sm mb-4">
        Set your password and profile details to finish onboarding.
      </p>

      <form class="grid grid-cols-1 gap-3" @submit.prevent="submit">
        <input
          v-model="email"
          type="email"
          placeholder="Email"
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
          disabled
        />
        <input
          v-model="firstName"
          placeholder="First name"
          required
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        />
        <input
          v-model="lastName"
          placeholder="Last name"
          required
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        />
        <input
          v-model="mobile"
          placeholder="Mobile"
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        />
        <input
          v-model="password"
          type="password"
          placeholder="Set password"
          required
          minlength="8"
          class="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
        />
        <div v-if="requireAal2" class="mt-2">
          <p class="text-xs text-slate-400 mb-2">Enter your 2FA code to continue.</p>
          <div class="flex gap-2">
            <input
              v-model="totpCode"
              placeholder="6-digit code"
              class="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white"
            />
            <button
              type="button"
              @click="verifyMfa"
              class="px-3 py-2 bg-slate-700 text-white rounded-lg"
              :disabled="verifying"
            >
              {{ verifying ? 'Verifying…' : 'Verify' }}
            </button>
          </div>
        </div>
        <button type="submit" class="px-4 py-2 btn-primary rounded-lg" :disabled="loading">
          {{ loading ? 'Saving…' : 'Finish' }}
        </button>
      </form>
      <p v-if="message" class="text-slate-400 text-sm mt-3">{{ message }}</p>
      <p v-if="error" class="text-red-400 text-sm mt-3">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { supabase } from '@/lib/supabaseClient'

defineOptions({
  name: 'AuthOnboardView',
})

const router = useRouter()
const email = ref('')
const firstName = ref('')
const lastName = ref('')
const mobile = ref('')
const password = ref('')
const loading = ref(false)
const message = ref('')
const error = ref('')
const requireAal2 = ref(false)
const factorId = ref('')
const totpCode = ref('')
const verifying = ref(false)

onMounted(async () => {
  // Finalize session from PKCE code or implicit hash tokens
  try {
    const url = new URL(window.location.href)
    // 1) Email token hash (works even if PKCE verifier missing)
    const tokenHash = url.searchParams.get('token_hash')
    const tokenType = (url.searchParams.get('type') || 'recovery') as
      | 'recovery'
      | 'invite'
      | 'magiclink'
    if (tokenHash) {
      const { error: vErr } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: tokenType,
      })
      if (!vErr) {
        try {
          history.replaceState(null, '', window.location.pathname)
        } catch {}
      }
    }
    // 2) PKCE code exchange
    const code = url.searchParams.get('code')
    if (code && !tokenHash) {
      const { error: exErr } = await supabase.auth.exchangeCodeForSession(code)
      if (!exErr) {
        try {
          history.replaceState(null, '', window.location.pathname)
        } catch {}
      }
    } else if (window.location.hash && window.location.hash.includes('access_token=')) {
      const params = new URLSearchParams(window.location.hash.slice(1))
      const access_token = params.get('access_token') || ''
      const refresh_token = params.get('refresh_token') || ''
      if (access_token && refresh_token) {
        await supabase.auth.setSession({ access_token, refresh_token })
        try {
          history.replaceState(null, '', window.location.pathname)
        } catch {}
      }
    }
  } catch {}

  const { data } = await supabase.auth.getUser()
  email.value = data.user?.email || ''
  const meta = data.user?.user_metadata || {}
  firstName.value = meta.first_name || ''
  lastName.value = meta.last_name || ''
  mobile.value = meta.mobile || ''
  // If user has TOTP enrolled but session is AAL1, require code
  try {
    const aalRes = await supabase.auth.mfa.getAuthenticatorAssuranceLevel?.()
    const currentLevel =
      aalRes?.data?.currentLevel || (data.user as { aal?: string } | undefined)?.aal || 'aal1'
    const lf = await supabase.auth.mfa.listFactors()
    const totp = (lf.data?.totp || []).find((f) => f.status === 'verified')
    if (String(currentLevel).toLowerCase() !== 'aal2' && totp) {
      factorId.value = totp.id
      requireAal2.value = true
    }
  } catch {}
})

async function submit() {
  loading.value = true
  error.value = ''
  message.value = ''
  // If AAL2 required and not yet elevated, ask for code
  if (requireAal2.value) {
    loading.value = false
    error.value = 'A 2FA code is required to finish. Enter it above and click Verify.'
    return
  }
  const { error: e1 } = await supabase.auth.updateUser({
    password: password.value,
    data: { first_name: firstName.value, last_name: lastName.value, mobile: mobile.value },
  })
  loading.value = false
  if (e1) {
    error.value = e1.message
    return
  }
  try {
    window.dispatchEvent(
      new CustomEvent('toast', { detail: { type: 'success', message: 'Profile updated.' } }),
    )
  } catch {}
  router.push('/mfa')
}

async function verifyMfa() {
  verifying.value = true
  error.value = ''
  try {
    if (!factorId.value) {
      const lf = await supabase.auth.mfa.listFactors()
      const totp = (lf.data?.totp || []).find((f) => f.status === 'verified')
      factorId.value = totp?.id || ''
    }
    if (!factorId.value) {
      error.value = 'No verified TOTP factor found.'
      verifying.value = false
      return
    }
    const ch = await supabase.auth.mfa.challenge({ factorId: factorId.value })
    if (ch.error) {
      error.value = ch.error.message
      verifying.value = false
      return
    }
    const ver = await supabase.auth.mfa.verify({
      factorId: factorId.value,
      challengeId: ch.data.id,
      code: totpCode.value,
    })
    if (ver.error) {
      error.value = ver.error.message
      verifying.value = false
      return
    }
    requireAal2.value = false
    verifying.value = false
    message.value = '2FA verified. Click Finish to complete.'
  } catch (e) {
    verifying.value = false
    error.value = e instanceof Error ? e.message : String(e)
  }
}
</script>
